import { FC, useEffect, useState } from "react";
import { Card, Shimmer, Stack } from "@altimateai/lego";

const SLOW_RESPONSE_THRESHOLD_MS = 10000;
const TIMEOUT_THRESHOLD_MS = 120000; // 2 minutes
const INITIAL_MESSAGE =
  "Initialising. Please wait for 10-15 seconds while we process your request...";
const SLOW_RESPONSE_MESSAGE =
  "Hm, this seems to be taking more time than we anticipated. Please check again after a few minutes...";

interface InitialResponseStatusProps {
  /** Called once when the response exceeds TIMEOUT_THRESHOLD_MS — used to cancel the in-flight request. */
  onTimeout?: () => void;
  /** Whether this is the first message in the session. Controls the initial "Initialising..." text. */
  isInitialResponse?: boolean;
}

/**
 * Renders the initial-response status while waiting for the agent to start streaming:
 *   1. Shows spinner + "Initialising..." message immediately on mount
 *   2. After SLOW_RESPONSE_THRESHOLD_MS, replaces text with the slow-response shimmer
 *   3. After TIMEOUT_THRESHOLD_MS, calls onTimeout (typically to cancel the request);
 *      parent should render TimeoutErrorMessage in its place after that
 *
 * Mounted/unmounted by the parent based on loading + initial-response conditions;
 * resets its timers on remount.
 */
export const SlowResponseShimmer: FC<InitialResponseStatusProps> = ({
  onTimeout,
  isInitialResponse = false,
}) => {
  const [phase, setPhase] = useState<"initial" | "slow">("initial");

  useEffect(() => {
    const slowTimer = setTimeout(() => setPhase("slow"), SLOW_RESPONSE_THRESHOLD_MS);
    const timeoutTimer = setTimeout(() => {
      onTimeout?.();
    }, TIMEOUT_THRESHOLD_MS);
    return () => {
      clearTimeout(slowTimer);
      clearTimeout(timeoutTimer);
    };
  }, [onTimeout]);

  return (
    <Card className="al-p-4 al-mb-4 al-border-0 al-shadow-none">
      <Stack direction="row" align="center" gap="sm" role="status" aria-live="polite">
        <div
          className="al-animate-spin al-h-4 al-w-4 al-border-2 al-border-primary al-border-t-transparent al-rounded-full al-shrink-0"
          role="progressbar"
          aria-label="Loading"
          aria-hidden="false"
        />
        {phase === "initial" ? (
          isInitialResponse ? (
            <span className="al-italic al-text-sm al-text-muted-foreground">{INITIAL_MESSAGE}</span>
          ) : (
            <span className="al-sr-only">Loading response</span>
          )
        ) : (
          <Shimmer as="span" className="al-italic al-text-sm">
            {SLOW_RESPONSE_MESSAGE}
          </Shimmer>
        )}
      </Stack>
    </Card>
  );
};
